import { Outlet, NavLink } from 'react-router-dom'
import { FileText, Wrench, Receipt, LogOut } from 'lucide-react'
import { signOut } from '@fleetops/firebase'
import { Avatar, NolensMark, NolensWordmark, cn } from '@fleetops/ui'
import { useSession } from '@/store/auth.store'
import { useNavigate } from 'react-router-dom'
import { ErrorBoundary } from '@/components/ErrorBoundary'

const NAV_ITEMS = [
  { to: '/supplier-portal/damages', label: 'Avarias', icon: FileText },
  { to: '/supplier-portal/maintenance', label: 'Manutenção', icon: Wrench },
  { to: '/supplier-portal/invoices', label: 'Faturas', icon: Receipt },
]

export function SupplierLayout() {
  const session = useSession()
  const navigate = useNavigate()

  async function handleSignOut() {
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <div className="flex h-screen bg-slate-50">
      <aside className="flex w-14 flex-col border-r border-slate-800 bg-slate-900 py-4 lg:w-52 lg:px-3 [&_:focus-visible]:ring-accent-400 [&_:focus-visible]:ring-offset-slate-900">
        {/* Brand */}
        <div className="mb-6 flex items-center gap-2.5 px-2 lg:px-0">
          <NolensMark size={32} />
          <div className="sr-only lg:not-sr-only">
            <p className="text-sm text-white">
              <NolensWordmark product="fleet" />
            </p>
            <p className="mt-1 text-[10px] text-slate-400">Portal Fornecedor</p>
          </div>
        </div>

        <nav className="flex flex-col gap-0.5">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'group flex h-8 items-center gap-2.5 rounded-lg px-2 text-sm font-medium transition-colors lg:px-2.5',
                  isActive
                    ? 'bg-white/10 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <div className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px]',
                    isActive ? 'bg-accent-400 text-slate-900' : 'bg-slate-800 text-slate-400 group-hover:bg-slate-700 group-hover:text-slate-200',
                  )}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <span className="sr-only lg:not-sr-only">{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex-1" />

        <div className="flex flex-col gap-1 border-t border-slate-800 pt-3">
          {session && (
            <div className="flex flex-col items-center gap-2 rounded-lg px-2 py-1.5 lg:flex-row lg:px-2.5">
              <Avatar name={session.email} size="sm" />
              <div className="hidden min-w-0 flex-1 lg:block">
                <p className="truncate text-xs font-medium text-slate-300">{session.email}</p>
                <p className="text-[10px] text-slate-400">Fornecedor</p>
              </div>
              <button
                onClick={handleSignOut}
                className="shrink-0 rounded p-1 text-slate-400 transition-colors hover:text-slate-200"
                title="Terminar sessão"
                aria-label="Terminar sessão"
              >
                <LogOut className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          )}
          <p className="hidden px-2.5 pt-1 text-[10px] text-slate-400 lg:block">
            Desenvolvido por rdoria.com
          </p>
        </div>
      </aside>

      <main className="flex flex-1 flex-col overflow-hidden">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  )
}
